import { FC, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Modal, Row, Tabs } from 'antd';
import { TbArrowLeft, TbEye, TbInfoCircle, TbListDetails, TbSettings, TbX } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useAlerts, useCreateAlert, useTestAlert, useUpdateAlert } from '@/queries';
import { isApiServiceError } from '@/services/http/errors';
import { AlertIcon, CreateAlertIcon, OurDocsLink, SmallTag } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import {
    PreferenceModalHeader,
    PreferenceModalSiderOption,
} from '@/shared/components/modals/PreferenceModalPrimitives';
import InlineIconLabel from '@/shared/components/modals/ResourceCreationModals/CreateOAuthApplicationModal/components/InlineIconLabel';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { AlertCatalog, AlertInput, AlertTestResult, WorkspaceAlert } from '@/types';

import AlertForm, { AlertFormSurface } from '../AlertForm';
import AlertManagementList, { AlertManagementSurface } from '../AlertManagementList';
import { reportAlertMutationFailure } from '../alertMutationFeedback';

type AlertModalView = 'create' | 'manage';

interface AlertEditorModalProps {
    catalog: AlertCatalog;
    initialAlert?: WorkspaceAlert;
    initialInput?: Partial<AlertInput>;
    initialManageSurface?: AlertManagementSurface;
    initialView?: AlertModalView;
    open: boolean;
    onClose: () => void;
    onDeleted?: (alert: WorkspaceAlert) => void;
    onSaved?: (alert: WorkspaceAlert, created: boolean) => void;
    onSelectAlert?: (alert: WorkspaceAlert, surface: AlertManagementSurface) => void;
}

const AlertEditorModal: FC<AlertEditorModalProps> = ({
    catalog,
    initialAlert,
    initialInput,
    initialManageSurface = 'properties',
    initialView = 'create',
    onClose,
    onDeleted,
    onSaved,
    onSelectAlert,
    open,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const alertCountQuery = useAlerts({ limit: 1 });
    const createAlert = useCreateAlert();
    const testAlert = useTestAlert();
    const updateAlert = useUpdateAlert();
    const [view, setView] = useState<AlertModalView>(initialView);
    const [selectedAlert, setSelectedAlert] = useState<WorkspaceAlert | undefined>(initialAlert);
    const [manageSurface, setManageSurface] = useState<AlertManagementSurface>(initialManageSurface);

    const selectAlert = (alert: WorkspaceAlert, surface: AlertManagementSurface) => {
        setSelectedAlert(alert);
        setManageSurface(surface);
        setView('manage');
        onSelectAlert?.(alert, surface);
    };

    const saveAlert = (input: AlertInput) => {
        const editingAlert = view === 'manage' ? selectedAlert : undefined;
        const wasCreated = !editingAlert;
        const mutation = editingAlert
            ? updateAlert.mutateAsync({ alertId: editingAlert.id, input, revision: editingAlert.revision })
            : createAlert.mutateAsync(input);

        void mutation
            .then((savedAlert) => {
                toast.success(wasCreated ? 'Alert created.' : 'Alert updated.');
                setSelectedAlert(savedAlert);
                setManageSurface('properties');
                setView('manage');
                onSaved?.(savedAlert, wasCreated);
            })
            .catch((error: unknown) => {
                reportAlertMutationFailure(error, {
                    fallbackMessage: 'The alert could not be saved.',
                    operation: wasCreated ? 'create' : 'update',
                });
                void alertCountQuery.refetch();
            });
    };

    const runAlertTest = async (input: AlertInput): Promise<AlertTestResult | undefined> => {
        const sourceAlertId = view === 'manage' ? selectedAlert?.id : undefined;
        try {
            const result = await testAlert.mutateAsync({ alert: input, sourceAlertId });
            const destinationCount = result.deliveredMethods.length;
            const description = `${result.matchCount} accessible record${result.matchCount === 1 ? '' : 's'} matched.`;

            if (result.failedMethods.length) {
                toast.warning('The test alert was only partially delivered.', {
                    description: `${result.failedMethods.join(', ')} failed. ${description}`,
                });
            } else if (destinationCount === 0) {
                toast.warning('All selected notification channels are currently disabled.', { description });
            } else {
                toast.success(
                    `Test alert sent immediately through ${destinationCount} destination${destinationCount === 1 ? '' : 's'}.`,
                    { description },
                );
            }
            return result;
        } catch (error) {
            const rateLimited = isApiServiceError(error) && error.code === 'RATE_LIMITED';
            if (!rateLimited) {
                captureException(error, {
                    extra: { category: input.category, deliveryMethods: input.deliveryMethods },
                });
            }
            toast.error(
                rateLimited
                    ? 'Test alerts are limited to three attempts per minute. Try again shortly.'
                    : 'The alert configuration test failed.',
            );
            return undefined;
        }
    };

    const manageContent = selectedAlert ? (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, padding: '1rem' }}>
            <Row align='middle' justify='space-between' style={{ marginBottom: '0.75rem' }}>
                <Col flex='none'>
                    <Button icon={<TbArrowLeft />} size='small' onClick={() => setSelectedAlert(undefined)}>
                        Back to Alerts
                    </Button>
                </Col>
                <Col flex='none'>
                    <SmallTag
                        accentColor={selectedAlert.enabled ? token.colorSuccess : undefined}
                        message={selectedAlert.enabled ? 'Active' : 'Paused'}
                        styles={{ marginLeft: 0 }}
                    />
                </Col>
            </Row>
            <Tabs
                activeKey={manageSurface}
                items={[
                    {
                        key: 'properties',
                        label: <InlineIconLabel icon={<TbSettings />} label='Manage Properties' />,
                    },
                    {
                        key: 'preview',
                        label: <InlineIconLabel icon={<TbEye />} label='Preview' />,
                    },
                ]}
                size='small'
                tabBarStyle={{ marginBottom: '0.75rem' }}
                onChange={(surface) => setManageSurface(surface as AlertManagementSurface)}
            />
            <div style={{ flex: 1, minHeight: 0 }}>
                <AlertForm
                    key={selectedAlert.id}
                    alert={selectedAlert}
                    catalog={catalog}
                    saving={updateAlert.isPending}
                    showSurfaceSwitcher={false}
                    surface={manageSurface as AlertFormSurface}
                    testing={testAlert.isPending}
                    onCancel={() => setSelectedAlert(undefined)}
                    onSubmit={saveAlert}
                    onSurfaceChange={(surface) => setManageSurface(surface as AlertManagementSurface)}
                    onTest={runAlertTest}
                />
            </div>
        </div>
    ) : (
        <div style={{ height: '100%', minHeight: 0, overflow: 'hidden', padding: '1rem' }}>
            <AlertManagementList
                catalog={catalog}
                height='calc(54rem - 15rem)'
                onDeleted={onDeleted}
                onOpen={selectAlert}
            />
        </div>
    );

    return (
        <Modal
            destroyOnHidden
            mask={false}
            open={open}
            width='min(88rem, calc(100vw - 4rem))'
            closable={false}
            closeIcon={null}
            footer={null}
            styles={{
                body: { height: '54rem', maxHeight: 'calc(100vh - 6rem)', overflow: 'hidden' },
                content: { border: `1px solid ${token.colorBorder}`, margin: 0, overflowX: 'hidden', padding: 0 },
            }}
            onCancel={onClose}
        >
            <Row wrap={false} style={{ height: '100%' }}>
                <Col span={7} style={{ height: '100%' }}>
                    <div
                        style={{
                            backgroundColor: token.colorBgBase,
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                        }}
                    >
                        <div>
                            <PreferenceModalHeader description='Monitor governed platform signals.' title='Alerts' />
                            <Tabs
                                activeKey={view}
                                size='small'
                                items={[
                                    {
                                        key: 'create',
                                        label: <InlineIconLabel icon={<CreateAlertIcon />} label='Create' />,
                                    },
                                    {
                                        key: 'manage',
                                        label: (
                                            <span style={{ alignItems: 'center', display: 'inline-flex' }}>
                                                <InlineIconLabel icon={<TbListDetails />} label='Manage' />
                                                <SmallTag
                                                    compact
                                                    message={alertCountQuery.data?.total ?? 0}
                                                    styles={{ marginLeft: '0.375rem' }}
                                                />
                                            </span>
                                        ),
                                    },
                                ]}
                                tabBarStyle={{ marginBottom: '0.75rem', padding: '0 1rem' }}
                                onChange={(nextView) => setView(nextView as AlertModalView)}
                            />
                            <div style={{ padding: '0 1rem' }}>
                                <PreferenceModalSiderOption active>
                                    <InlineIconLabel
                                        icon={view === 'create' ? <AlertIcon /> : <TbListDetails />}
                                        label={
                                            view === 'create'
                                                ? 'Event-Driven Alert'
                                                : (selectedAlert?.name ?? 'All Alerts')
                                        }
                                    />
                                </PreferenceModalSiderOption>
                            </div>
                        </div>
                        <div style={{ marginTop: 'auto', padding: '1rem' }}>
                            <PlatformAlert
                                title='Workspace-scoped evaluation'
                                showIcon
                                icon={<TbInfoCircle />}
                                status='info'
                                description={
                                    <span>
                                        Alert signatures are scoped to your active workspace and evaluated with your
                                        permissions. Learn more in <OurDocsLink />
                                    </span>
                                }
                            />
                        </div>
                    </div>
                </Col>
                <Col flex='auto' style={{ borderLeft: `1px solid ${token.colorBorder}`, height: '100%', minWidth: 0 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
                        <PreferenceModalHeader
                            title={
                                view === 'create'
                                    ? 'Create Alert'
                                    : selectedAlert
                                      ? selectedAlert.name
                                      : 'Manage Alerts'
                            }
                            titleLevel={4}
                            description={
                                view === 'create'
                                    ? 'Define a governed signal, delivery destination, and evaluation cadence.'
                                    : selectedAlert
                                      ? 'Manage alert properties and preview the governed delivery workflow.'
                                      : 'Open, pause, preview, or delete your alerts.'
                            }
                            extra={
                                <Button
                                    aria-label='Close alert editor'
                                    size='small'
                                    type='text'
                                    icon={<TbX />}
                                    onClick={onClose}
                                />
                            }
                        />
                        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                            {view === 'create' ? (
                                <div style={{ height: '100%', padding: '1rem' }}>
                                    <AlertForm
                                        key='new'
                                        catalog={catalog}
                                        initialInput={initialInput}
                                        saving={createAlert.isPending}
                                        testing={testAlert.isPending}
                                        onCancel={onClose}
                                        onSubmit={saveAlert}
                                        onTest={runAlertTest}
                                    />
                                </div>
                            ) : (
                                manageContent
                            )}
                        </div>
                    </div>
                </Col>
            </Row>
        </Modal>
    );
};

export default AlertEditorModal;
